import ArrowForwardIcon from '@material-design-icons/svg/filled/arrow_forward.svg?react';
import MailIcon from '@material-design-icons/svg/filled/mail.svg?react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { isLeftResult } from '../../lib/trpcTypeGuards';
import { unknownToLeft } from '../../lib/unknownToLeft';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import { getSafeFromLocation } from '../../utils/navigation';
import {
  getPublisherInviteStateClasses,
  getPublisherInviteStateLabel,
} from '../../utils/publisherInvites';
import { Button } from '../Button';
import { LayoutWithBgGradient } from '../LayoutWithBgGradient';
import { PageHeaderWithUser } from '../PageHeaderWithUser';

type PendingInvite = RightResult<
  RouterOutputs['listMyPendingPublisherInvites']
>['invites'][number];

export function PublisherAccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const trpc = useTRPC();
  const [, { refreshUser, setOnboardingState }] = useAuth();
  const { refresh: refreshPublisherContext } = usePublisher();
  const {
    data: invites,
    isLoading: loading,
    run,
  } = useAsyncData<PendingInvite[]>({
    initialData: [],
    initialLoading: true,
  });
  const [inviteActionId, setInviteActionId] = useState<string | null>(null);

  usePageTitle('Publisher Access | Publishing Portal');

  async function loadInvites() {
    await run(
      () =>
        trpc.listMyPendingPublisherInvites
          .query(undefined)
          .catch(unknownToLeft),
      {
        onSuccess: (result, helpers) => {
          if (isLeftResult(result)) {
            toast.error('Failed to load pending invites', {
              description: result.left.message,
              duration: 5000,
            });
            captureAndReportError(result.left);
            return;
          }

          helpers.setData(result.right.invites);
        },
      },
    );
  }

  useEffect(() => {
    void loadInvites();
  }, []);

  async function handleAccept(inviteId: string) {
    setInviteActionId(inviteId);

    const result = await trpc.acceptPublisherInvite
      .mutate({ inviteId })
      .catch(unknownToLeft);

    setInviteActionId(null);

    if (isLeftResult(result)) {
      toast.error('Failed to accept invite', {
        description: result.left.message,
        duration: 5000,
      });
      captureAndReportError(result.left);
      return;
    }

    setOnboardingState('Complete');
    await refreshUser();
    await refreshPublisherContext();

    toast.success('Publisher access granted', {
      description:
        result.right.status === 'already_member'
          ? 'This publisher is already available in your workspace.'
          : 'The invitation was accepted successfully.',
      duration: 4000,
    });

    navigate(getSafeFromLocation(location.state?.from) ?? '/dashboard', {
      replace: true,
    });
  }

  async function handleDecline(inviteId: string) {
    setInviteActionId(inviteId);

    const result = await trpc.declinePublisherInvite
      .mutate({ inviteId })
      .catch(unknownToLeft);

    setInviteActionId(null);

    if (isLeftResult(result)) {
      toast.error('Failed to decline invite', {
        description: result.left.message,
        duration: 5000,
      });
      captureAndReportError(result.left);
      return;
    }

    toast.success('Invite declined');
    await loadInvites();
  }

  return (
    <LayoutWithBgGradient>
      <div className="min-h-screen w-screen overflow-y-auto">
        <PageHeaderWithUser />
        <div className="mx-auto flex max-w-4xl flex-col gap-8 px-8 py-16">
          <div className="space-y-3">
            <div className="text-sm font-semibold uppercase tracking-[0.24em] text-neutral-400">
              Publisher Access
            </div>
            <h1 className="text-4xl font-semibold text-white">
              Create or join a publisher to continue.
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-neutral-400">
              Your account is verified, but you do not have accepted publisher
              access yet. If someone invited you by email, you can accept that
              invite here. Otherwise, create a publisher profile to start
              managing dApps.
            </p>
          </div>

          <div className="rounded-2xl border border-neutral-800 bg-black/30 p-6">
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-white">
              <MailIcon className="size-4 fill-current" />
              Pending invites
            </div>

            {loading ? (
              <div className="text-sm text-neutral-400">
                Loading pending invites…
              </div>
            ) : invites.length === 0 ? (
              <div className="rounded-xl border border-dashed border-neutral-800 px-4 py-6 text-sm text-neutral-500">
                No pending publisher invites for this email.
              </div>
            ) : (
              <div className="space-y-4">
                {invites.map((invite) => (
                  <div
                    key={invite.id}
                    className="rounded-xl border border-neutral-800 bg-neutral-950/60 p-4"
                  >
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-white">
                          {invite.publisherName}
                        </div>
                        <div className="mt-1 text-sm text-neutral-400">
                          {invite.publisherType} publisher • {invite.role}{' '}
                          access
                        </div>
                      </div>

                      <div
                        className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${getPublisherInviteStateClasses(invite.state)}`}
                      >
                        {getPublisherInviteStateLabel(invite.state)}
                      </div>
                    </div>

                    <div className="mb-4 text-xs text-neutral-500">
                      Invited email: {invite.invitedEmail} • Expires{' '}
                      {new Date(invite.expiresAt).toLocaleString()}
                    </div>

                    <div className="flex flex-wrap gap-3">
                      {invite.state === 'Pending' && (
                        <Button
                          type="button"
                          disabled={inviteActionId === invite.id}
                          onClick={() => void handleAccept(invite.id)}
                        >
                          Accept Invite
                        </Button>
                      )}
                      {invite.state === 'Pending' && (
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={inviteActionId === invite.id}
                          onClick={() => void handleDecline(invite.id)}
                        >
                          Decline
                        </Button>
                      )}
                      {invite.state !== 'Pending' && (
                        <div className="text-sm text-neutral-500">
                          {invite.state === 'Expired'
                            ? 'This invite has expired. Ask a team manager to send a fresh invite.'
                            : 'This invite requires publisher support before it can be accepted.'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-neutral-800 bg-black/30 p-6">
            <div className="mb-4 text-sm font-semibold text-white">
              Next step
            </div>
            <p className="mb-6 max-w-xl text-sm leading-6 text-neutral-400">
              Creating a publisher will make it your active workspace
              immediately. Verification stays scoped to that publisher.
            </p>
            <Button
              className="inline-flex items-center gap-2"
              onClick={() => navigate('/create-profile')}
            >
              Create Publisher
              <ArrowForwardIcon className="size-4 fill-current" />
            </Button>
          </div>
        </div>
      </div>
    </LayoutWithBgGradient>
  );
}
